import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Display, H2, H3, H4, Lead, Small } from "@/components/ui/typography"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Trans, useTranslation } from "react-i18next"
import { ArrowRight, Phone, PhoneIncoming, ChevronsUpDown, Check } from "lucide-react"
import { motion } from "motion/react"
import {
  fadeInUp,
  staggerContainer,
  phoneEntrance,
  fadeInLeft,
  fadeInRight,
  scaleIn,
  viewportOnce,
  viewportOnceSmall,
} from "@/lib/animations"
import {
  ChartLineIcon,
  AirplaneTiltIcon,
  SunIcon,
  ShieldWarningIcon,
  KeyIcon,
  ClipboardTextIcon,
  PhoneCallIcon,
  ShieldCheckIcon,
  GlobeHemisphereWestIcon,
  WaveformIcon,
  MicrophoneIcon,
  WebhooksLogoIcon,
  ChartBarIcon,
  CloudIcon,
  CheckCircleIcon,
  RobotIcon,
} from "@phosphor-icons/react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useLang } from "@/hooks/useLang"
import { Flag } from "@/components/Flag"
import { useQuery } from "@tanstack/react-query"
import { pricingQueries } from "@/services/pricingApi"
import type { CountryRate } from "@/types/pricing"
import Arrow57 from "@/assets/57.svg?react"
import Arrow30 from "@/assets/30.svg?react"
import Deco13 from "@/assets/13.svg?react"
import Deco14 from "@/assets/14.svg?react"
import Logo from "@/components/Logo"
import { CTASection } from "@/components/CTASection"
import OpenClawIcon from "@/assets/openclaw-color.svg?react"
import OpenClawText from "@/assets/openclaw-text.svg?react"

function PricingFormula() {
  const { t } = useTranslation('openclaw')
  const lang = useLang()
  const { data: pricingData, isLoading } = useQuery(pricingQueries.pricing())
  const [selectedCountry, setSelectedCountry] = useState<CountryRate | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (pricingData && !selectedCountry) {
      const uk = pricingData.countries.find(c => c.countryCode === 'GB')
      const popular = pricingData.countries.find(c => c.popular)
      setSelectedCountry(uk ?? popular ?? pricingData.countries[0] ?? null)
    }
  }, [pricingData, selectedCountry])

  const agentFee = pricingData?.agentFeePerMin ?? null
  const callRate = selectedCountry?.landlineRate ?? null
  const total = callRate !== null && agentFee !== null ? callRate + agentFee : null

  const fmt = (rate: number | null) => {
    if (rate === null) return '—'
    return `£${parseFloat(rate.toFixed(4))}`
  }

  return (
    <section className="py-20 px-4 bg-muted/30">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <H2 className="mb-3">{t('openclaw.pricingFormula.title')}</H2>
          <Lead className="text-muted-foreground">{t('openclaw.pricingFormula.subtitle')}</Lead>
        </motion.div>

        <motion.div
          className="flex flex-col items-center gap-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Country picker */}
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <span className="text-sm text-muted-foreground">{t('openclaw.pricingFormula.exampleNote')}:</span>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 font-normal h-8">
                  {selectedCountry ? (
                    <>
                      <Flag countryCode={selectedCountry.countryCode} size="sm" />
                      {selectedCountry.country}
                    </>
                  ) : (
                    <span className="text-muted-foreground">{t('openclaw.pricingFormula.selectCountry')}</span>
                  )}
                  <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="center">
                <Command>
                  <CommandInput placeholder={t('openclaw.pricingFormula.searchCountries')} />
                  <CommandList>
                    <CommandEmpty>No countries found.</CommandEmpty>
                    <CommandGroup>
                      {(pricingData?.countries ?? []).map(country => (
                        <CommandItem
                          key={country.countryCode}
                          value={`${country.countryCode} ${country.country}`}
                          onSelect={() => {
                            setSelectedCountry(country)
                            setOpen(false)
                          }}
                        >
                          <Flag countryCode={country.countryCode} size="sm" className="mr-2 flex-shrink-0" />
                          <span className="truncate">{country.country}</span>
                          {country.countryCode === selectedCountry?.countryCode && (
                            <Check className="ml-auto h-4 w-4 flex-shrink-0" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Formula row */}
          <div className="flex items-center gap-3 sm:gap-5 flex-wrap justify-center">
            {/* Call rate */}
            <div className="flex flex-col items-center gap-3 bg-background border border-border rounded-2xl px-8 py-7 min-w-[148px] text-center shadow-sm">
              <PhoneCallIcon weight="duotone" className="w-7 h-7 text-primary" />
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="flex items-baseline gap-0.5">
                  <span className="text-3xl font-bold tracking-tight">{fmt(callRate)}</span>
                  <span className="text-sm text-muted-foreground">{t('openclaw.pricingFormula.perMin')}</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">{t('openclaw.pricingFormula.callRate')}</span>
            </div>

            {agentFee !== null && (
              <span className="text-2xl font-light text-muted-foreground">+</span>
            )}

            {/* Agent fee */}
            {agentFee !== null && (
              <div className="flex flex-col items-center gap-3 bg-primary text-primary-foreground rounded-2xl px-8 py-7 min-w-[148px] text-center shadow-sm">
                <RobotIcon weight="duotone" className="w-7 h-7" />
                {isLoading ? (
                  <Skeleton className="h-8 w-20 bg-primary-foreground/20" />
                ) : (
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-3xl font-bold tracking-tight">{fmt(agentFee)}</span>
                    <span className="text-sm text-primary-foreground/70">{t('openclaw.pricingFormula.perMin')}</span>
                  </div>
                )}
                <span className="text-xs text-primary-foreground/70 font-semibold uppercase tracking-widest">{t('openclaw.pricingFormula.agentFee')}</span>
              </div>
            )}

            {agentFee !== null && (
              <span className="text-2xl font-light text-muted-foreground">=</span>
            )}

            {/* Total */}
            {agentFee !== null && (
              <div className="flex flex-col items-center gap-3 bg-background border-2 border-primary/25 rounded-2xl px-8 py-7 min-w-[148px] text-center shadow-sm">
                {isLoading ? (
                  <Skeleton className="h-10 w-24" />
                ) : (
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-4xl font-bold tracking-tight text-primary">{fmt(total)}</span>
                    <span className="text-base text-muted-foreground">{t('openclaw.pricingFormula.perMin')}</span>
                  </div>
                )}
                <span className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">{t('openclaw.pricingFormula.total')}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm text-muted-foreground">{t('openclaw.pricingFormula.disclaimer')}</p>
            <Button variant="link" size="sm" asChild className="text-primary h-auto p-0">
              <Link to="/$lang/pricing" params={{ lang }}>
                {t('openclaw.pricingFormula.cta')}
                <ArrowRight className="ml-1 w-3.5 h-3.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export const Route = createFileRoute('/$lang/openclaw/')({
  staticData: {
    title: 'OpenClaw Voice — PhoneSalad',
    description: 'Give your AI agent a real phone. OpenClaw + PhoneSalad enables AI-powered voice calls across 200+ countries.',
    sitemap: { priority: '0.8', changefreq: 'monthly' },
  },
  component: OpenClawPage,
})

function OpenClawPage() {
  const { t } = useTranslation('openclaw')
  const lang = useLang()

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-20 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,var(--color-primary)_0%,transparent_70%)] opacity-10 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_100%,var(--color-primary)_0%,transparent_50%)] opacity-5 pointer-events-none" />

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div className="text-center lg:text-left lg:pr-0 lg:-mr-12">
              <div
                className="flex items-center gap-3 mb-6 justify-center lg:justify-start animate-fade-in-up opacity-0"
                style={{ animationDelay: '100ms' }}
              >
                <div className="flex items-center gap-1.5">
                  <OpenClawIcon className="w-6 h-6" />
                  <OpenClawText className="h-4 text-foreground" />
                </div>
                <span className="text-muted-foreground font-medium text-sm">x</span>
                <Logo size="sm" linkTo="" />
              </div>
              <div
                className="relative inline-block animate-fade-in-up opacity-0"
                style={{ animationDelay: '200ms' }}
              >
                <Deco13 className="absolute w-12 h-12 text-primary/30 -left-16 top-0 hidden lg:block" />
                <Deco14 className="absolute w-10 h-10 text-primary/30 -right-8 -top-2 hidden lg:block" />
                <Display className="mb-6 leading-tight">
                  <Trans
                    i18nKey="openclaw.hero.title" ns="openclaw"
                    components={{ hl: <span className="text-primary" /> }}
                  />
                </Display>
              </div>
              <div
                className="animate-fade-in-up opacity-0"
                style={{ animationDelay: '320ms' }}
              >
                <Lead className="mb-8 max-w-lg mx-auto lg:mx-0">
                  {t('openclaw.hero.description')}
                </Lead>
              </div>
              <div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up opacity-0"
                style={{ animationDelay: '440ms' }}
              >
                <Button size="lg" className="text-lg px-8 py-6 group" asChild>
                  <Link to="/$lang/openclaw/setup" params={{ lang }} className="flex items-center gap-2">
                    {t('openclaw.hero.cta')}
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8 py-6" asChild>
                  <Link to="/$lang/pricing" params={{ lang }}>
                    {t('openclaw.hero.viewPricing')}
                  </Link>
                </Button>
              </div>
            </div>

            {/* Phone Mockup — CSS entrance, JS for continuous animations */}
            <div
              className="relative flex justify-center animate-scale-in-up opacity-0"
              style={{ animationDelay: '200ms' }}
            >
              <div className="relative w-[280px] h-[560px] rounded-[3rem] border-[6px] border-neutral-800 bg-neutral-900 shadow-2xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-neutral-900 to-neutral-900" />
                <div className="relative h-full p-8 flex flex-col justify-between">
                  <div className="space-y-2 pt-12 text-center">
                    <motion.div
                      className="w-20 h-20 bg-primary/20 rounded-full mx-auto flex items-center justify-center mb-4"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <PhoneIncoming className="w-10 h-10 text-primary" />
                    </motion.div>
                    <h3 className="text-white text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('openclaw.hero.phoneMockup.agentName')}</h3>
                    <p className="text-primary text-sm animate-pulse">{t('openclaw.hero.phoneMockup.incomingCall')}...</p>
                  </div>
                  <div className="flex justify-around pb-12">
                    <div className="w-16 h-16 rounded-full bg-destructive flex items-center justify-center shadow-lg">
                      <Phone className="w-7 h-7 text-white rotate-[135deg]" />
                    </div>
                    <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                      <Phone className="w-7 h-7 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Cards — JS for continuous float */}
              <motion.div
                className="absolute -top-2 right-2 lg:-top-6 lg:right-0 bg-background/80 backdrop-blur-xl p-3 lg:p-5 rounded-xl lg:rounded-2xl shadow-xl w-48 lg:w-60 border border-border/40 z-10"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0, y: [-8, 8, -8] }}
                transition={{
                  opacity: { delay: 0.6, duration: 0.5 },
                  x: { delay: 0.6, duration: 0.5 },
                  y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.8 }
                }}
              >
                <div className="flex items-center gap-2 lg:gap-3 mb-1">
                  <ChartLineIcon weight="duotone" className="w-4 h-4 lg:w-5 lg:h-5 text-primary" />
                  <Small className="font-bold text-xs lg:text-sm text-foreground">{t('openclaw.hero.phoneMockup.floatingCard1')}</Small>
                </div>
                <p className="text-[10px] lg:text-xs text-foreground/80">{t('openclaw.hero.phoneMockup.floatingCard1Description')}</p>
              </motion.div>
              <motion.div
                className="absolute bottom-28 -left-2 lg:bottom-20 lg:-left-10 bg-background/80 backdrop-blur-xl p-3 lg:p-5 rounded-xl lg:rounded-2xl shadow-xl w-48 lg:w-60 border border-border/40 z-10"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0, y: [8, -8, 8] }}
                transition={{
                  opacity: { delay: 0.8, duration: 0.5 },
                  x: { delay: 0.8, duration: 0.5 },
                  y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }
                }}
              >
                <div className="flex items-center gap-2 lg:gap-3 mb-1">
                  <SunIcon weight="duotone" className="w-4 h-4 lg:w-5 lg:h-5 text-primary" />
                  <Small className="font-bold text-xs lg:text-sm text-foreground">{t('openclaw.hero.phoneMockup.floatingCard2')}</Small>
                </div>
                <p className="text-[10px] lg:text-xs text-foreground/80">{t('openclaw.hero.phoneMockup.floatingCard2Description')}</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Bento Grid */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <H2 className="mb-4">{t('openclaw.useCases.title')}</H2>
            <Lead>{t('openclaw.useCases.description')}</Lead>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {[
              { icon: ChartLineIcon, key: 'smartAlerts', color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
              { icon: AirplaneTiltIcon, key: 'travelUpdates', color: 'text-sky-600', bg: 'bg-sky-100 dark:bg-sky-900/30' },
              { icon: SunIcon, key: 'dailyBriefings', color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30' },
              { icon: ShieldWarningIcon, key: 'emergency', color: 'text-rose-600', bg: 'bg-rose-100 dark:bg-rose-900/30' },
            ].map(({ icon: Icon, key, color, bg }) => (
              <motion.div key={key} variants={fadeInUp}>
                <Card variant="outline" className="flex flex-col h-full">
                  <CardContent className="flex flex-col flex-1">
                    <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center mb-4`}>
                      <Icon weight="duotone" className={`w-5 h-5 ${color}`} />
                    </div>
                    <H4 className="mb-2 leading-snug agent-quote">{t(`openclaw.useCases.${key}.title`)}</H4>
                    <Small className="text-muted-foreground mt-auto">{t(`openclaw.useCases.${key}.description`)}</Small>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <motion.div
            className="text-center mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Button variant="outline" size="lg" asChild>
              <Link to="/$lang/openclaw/use-cases" params={{ lang }}>
                {t('openclaw.viewMoreUseCases')} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Call Experience — full-bleed accent */}
      <section className="bg-primary text-primary-foreground py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div
              className="lg:w-1/2 space-y-8 text-center lg:text-left"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <H2 className="leading-tight text-primary-foreground">{t('openclaw.callExperience.title')}</H2>
              <div className="space-y-6">
                {[
                  { key: 'naturalVoice', desc: 'naturalVoiceDescription' },
                  { key: 'twoWay', desc: 'twoWayDescription' },
                  { key: 'agentCapabilities', desc: 'agentCapabilitiesDescription' },
                ].map(({ key, desc }, index) => (
                  <motion.div
                    key={key}
                    className="flex gap-4 justify-center lg:justify-start"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, duration: 0.5 }}
                  >
                    <CheckCircleIcon weight="duotone" className="w-6 h-6 text-primary-foreground/80 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-lg" style={{ fontFamily: 'var(--font-heading)' }}>{t(`openclaw.callExperience.${key}`)}</p>
                      <p className="text-primary-foreground/80">{t(`openclaw.callExperience.${desc}`)}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              className="lg:w-1/2 flex justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <div className="relative w-64 h-64 rounded-full bg-primary-foreground/10 flex items-center justify-center">
                <div className="w-48 h-48 rounded-full bg-primary-foreground/15 flex items-center justify-center">
                  <div className="relative w-32 h-32 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                    <div className="absolute inset-0 flex items-center justify-center gap-1.5 pointer-events-none">
                      {[12, 24, 32, 20, 28].map((h, i) => (
                        <motion.div
                          key={i}
                          className="w-1 bg-primary-foreground/40 rounded-full"
                          style={{ height: h }}
                          animate={{
                            scaleY: [1, 1.5, 0.8, 1.2, 1],
                            opacity: [0.4, 0.8, 0.4, 0.7, 0.4]
                          }}
                          transition={{
                            duration: 1.2,
                            repeat: Infinity,
                            delay: i * 0.15,
                            ease: "easeInOut"
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <H2 className="mb-16">{t('openclaw.howItWorks.title')}</H2>
          </motion.div>

          <div className="relative grid md:grid-cols-3 gap-12">
            {[
              { icon: KeyIcon, key: 'getKey', step: '1' },
              { icon: ClipboardTextIcon, key: 'pastePrompt', step: '2' },
              { icon: PhoneCallIcon, key: 'firstCall', step: '3' },
            ].map(({ icon: Icon, key, step }, index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="bg-primary/5 rounded-2xl px-8 py-10 flex flex-col items-center justify-center gap-4 mb-6 aspect-square">
                  <span className="text-sm font-semibold text-primary italic">{t('openclaw.howItWorks.steps.step', { step })}</span>
                  <Icon weight="duotone" className="w-14 h-14" style={{ color: 'var(--color-primary)' }} />
                  <H4>{t(`openclaw.howItWorks.steps.${key}.title`)}</H4>
                </div>
                <p className="text-muted-foreground text-sm">{t(`openclaw.howItWorks.steps.${key}.description`)}</p>
              </motion.div>
            ))}

            <Arrow57 className="hidden md:block absolute w-16 h-32 text-primary/30 left-[30%] top-16 -translate-x-1/2 rotate-270" />
            <Arrow30 className="hidden md:block absolute w-8 h-32 text-primary/30 left-[66%] top-16 -translate-x-1/2 rotate-270" />
          </div>
        </div>
      </section>

      {/* Key Features + Trust */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <H2 className="mb-4">{t('openclaw.features.title')}</H2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {[
                { icon: WaveformIcon, key: 'lowLatency' },
                { icon: GlobeHemisphereWestIcon, key: 'multiLanguage' },
                { icon: MicrophoneIcon, key: 'callRecording' },
                { icon: WebhooksLogoIcon, key: 'webhooks' },
                { icon: ChartBarIcon, key: 'analytics' },
                { icon: CloudIcon, key: 'scalable' },
              ].map(({ icon: Icon, key }) => (
                <motion.div key={key} className="flex gap-4" variants={fadeInUp}>
                  <Icon weight="duotone" className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t(`openclaw.features.${key}.title`)}</p>
                    <p className="text-sm text-muted-foreground">{t(`openclaw.features.${key}.description`)}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card variant="outline" className="bg-background/60 backdrop-blur-xl">
                <CardContent className="p-10 space-y-6">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <ShieldCheckIcon weight="duotone" className="w-8 h-8 text-primary" />
                  </div>
                  <H3>{t('openclaw.features.enterprise.title')}</H3>
                  <p className="text-muted-foreground leading-relaxed">{t('openclaw.features.enterprise.description')}</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <PricingFormula />

      {/* FAQ + Pricing */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* FAQ */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <H2 className="mb-8">{t('openclaw.faq.title')}</H2>
              <Accordion type="single" collapsible className="space-y-4">
                {(Array.isArray(t('openclaw.faq.items', { returnObjects: true })) ? t('openclaw.faq.items', { returnObjects: true }) as { id: string; question: string; answer: string }[] : []).map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                  >
                    <AccordionItem
                      value={item.id}
                      className="group border rounded-xl bg-card px-6"
                    >
                      <AccordionTrigger className="text-lg hover:no-underline">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </Accordion>
            </motion.div>

            {/* Pricing Card */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="lg:sticky lg:top-24">
                <Card className="bg-primary text-primary-foreground border-0 rounded-3xl shadow-xl">
                  <CardContent className="p-10 flex flex-col justify-between">
                    <div className="space-y-6">
                      <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground border-0 uppercase tracking-widest text-xs font-bold">
                        {t('openclaw.pricing.badge')}
                      </Badge>
                      <H3 className="text-primary-foreground">{t('openclaw.pricing.title')}</H3>
                      <p className="text-primary-foreground/80 text-lg">{t('openclaw.pricing.description')}</p>
                    </div>
                    <div className="mt-10">
                      <Button size="lg" variant="secondary" className="w-full text-lg py-6 group" asChild>
                        <Link to="/$lang/pricing" params={{ lang }} className="flex items-center justify-center gap-2">
                          {t('openclaw.pricing.cta')}
                          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <CTASection
        variant="dark"
        translationKeyRoot="openclaw.cta" ns="openclaw"
        buttonTo="/$lang/openclaw/setup"
        buttonParams={{ lang }}
      />
    </div>
  )
}
